import { z } from "zod";

/**
 * School website — the contract between the admin editor (apps/web `/website`),
 * the API (`routes/school-website`) and the static sites (apps/school-website).
 *
 * ONE schema, imported by the API and the web form, so "Zod on both client and
 * server" is one schema rather than two that drift.
 *
 * The site itself is a static bundle (CloudFront). It reads this content plus the
 * school's published posts (flyers, admission offers, events, news, achievements)
 * from `GET /api/v1/school-website/public/:schoolCode` at page load, so a post
 * shows up on the site without a rebuild.
 */

/** Templates the static app ships. Add the id here AND a folder under apps/school-website/src/templates. */
export const SCHOOL_WEBSITE_TEMPLATES = ["sky", "mint", "blossom", "sand", "aurora", "nexus"] as const;
export type SchoolWebsiteTemplate = (typeof SCHOOL_WEBSITE_TEMPLATES)[number];

export const SCHOOL_WEBSITE_TEMPLATE_INFO: Record<SchoolWebsiteTemplate, { name: string; blurb: string }> = {
  sky: { name: "Lumen Sky", blurb: "Light and airy — warm white, soft blue accents, elegant serif headings." },
  mint: { name: "Lumen Mint", blurb: "Light and fresh — soft green accents with a calm, natural feel." },
  blossom: { name: "Lumen Blossom", blurb: "Light and friendly — gentle pink accents, ideal for primary schools." },
  sand: { name: "Lumen Sand", blurb: "Light and classic — warm sand and amber accents, timeless and refined." },
  aurora: { name: "Aurora", blurb: "Bold uppercase headings, violet-to-blue gradients and gold accents." },
  nexus: { name: "Nexus", blurb: "Clean dark layout with cyan neon accents and an animated hero." },
};

export const SCHOOL_WEBSITE_STATUSES = ["DRAFT", "PUBLISHED"] as const;
export type SchoolWebsiteStatus = (typeof SCHOOL_WEBSITE_STATUSES)[number];

/**
 * Post kinds the site shows. ADMISSION = an admission offer / discount
 * ("Early-bird: 20% off admission fee till 31 Jan"). For FLYER and ADMISSION,
 * `eventDate` is read as "show until" — the public feed drops them after that day.
 */
export const SCHOOL_POST_KINDS = ["FLYER", "ADMISSION", "EVENT", "NEWS", "ACHIEVEMENT", "ACTIVITY"] as const;
export type SchoolPostKind = (typeof SCHOOL_POST_KINDS)[number];

// eslint-disable-next-line no-control-regex -- the point is to reject them
const CONTROL_CHARS = /[\u0000-\u001f\u007f-\u009f]/;
/** Our own upload proxy path, as `/upload/presign-post` hands it back. */
export const UPLOAD_FILE_PATH_RE = /^\/api\/v1\/upload\/file\/[^/?#\s]+$/;

/** An absolute http(s) URL with a host and no embedded credentials. */
export function isWebUrl(value: string): boolean {
  if (CONTROL_CHARS.test(value)) return false;
  try {
    const u = new URL(value);
    return (u.protocol === "http:" || u.protocol === "https:") && !!u.hostname && !u.username && !u.password;
  } catch {
    return false;
  }
}

/**
 * An image or file the site renders: either a web URL or one of our own
 * uploads. The API additionally checks that an upload path sits under the
 * caller's own school (`routes/school-website`), which the client cannot know.
 */
export const siteAssetUrl = z
  .string()
  .trim()
  .max(2048)
  .refine((v) => isWebUrl(v) || UPLOAD_FILE_PATH_RE.test(v), {
    message: "Upload an image or paste a link starting with https://",
  });

const text = (max: number) => z.string().trim().max(max).default("");

export const schoolWebsiteContentSchema = z.object({
  tagline: text(160),
  heroHeadline: text(120),
  heroSubheadline: text(300),
  heroImages: z.array(siteAssetUrl).max(6).default([]),
  about: text(4000),
  vision: text(600),
  mission: text(600),
  established: z.number().int().min(1800).max(2100).nullable().default(null),
  affiliation: text(160),
  stats: z
    .array(z.object({ value: z.string().trim().min(1).max(20), label: z.string().trim().min(1).max(60) }))
    .max(6)
    .default([]),
  programs: z
    .array(
      z.object({
        name: z.string().trim().min(1).max(80),
        grades: text(60),
        description: text(600),
      }),
    )
    .max(8)
    .default([]),
  facilities: z
    .array(z.object({ name: z.string().trim().min(1).max(80), description: text(400) }))
    .max(12)
    .default([]),
  testimonials: z
    .array(
      z.object({
        quote: z.string().trim().min(1).max(600),
        name: z.string().trim().min(1).max(80),
        role: text(80),
      }),
    )
    .max(8)
    .default([]),
  gallery: z
    .array(z.object({ src: siteAssetUrl, caption: text(160) }))
    .max(30)
    .default([]),
  admissions: z
    .object({
      open: z.boolean().default(true),
      year: text(20),
      description: text(1500),
      feeNote: text(400),
    })
    .default({}),
  officeHours: text(120),
  mapUrl: z.union([z.literal(""), siteAssetUrl.refine(isWebUrl, "Paste a Google Maps link")]).default(""),
  /** Principal's (or chairman's) message — shown on the home page once a message is written. */
  principal: z
    .object({
      name: text(80),
      title: text(80),
      message: text(1500),
      photo: z.union([z.literal(""), siteAssetUrl]).default(""),
    })
    .default({}),
  /** "Why choose us" — the school's own reasons, shown as feature cards. */
  highlights: z
    .array(z.object({ title: z.string().trim().min(1).max(60), description: text(240) }))
    .max(6)
    .default([]),
});

export type SchoolWebsiteContent = z.infer<typeof schoolWebsiteContentSchema>;

export const schoolWebsiteUpdateSchema = z.object({
  template: z.enum(SCHOOL_WEBSITE_TEMPLATES).optional(),
  status: z.enum(SCHOOL_WEBSITE_STATUSES).optional(),
  content: schoolWebsiteContentSchema.optional(),
});
export type SchoolWebsiteUpdate = z.infer<typeof schoolWebsiteUpdateSchema>;

/** Parse a stored content blob leniently: a bad or old blob reads as defaults, never a crash. */
export function readSchoolWebsiteContent(raw: unknown): SchoolWebsiteContent {
  let value: unknown = raw;
  if (typeof raw === "string") {
    try {
      value = JSON.parse(raw);
    } catch {
      value = {};
    }
  }
  const parsed = schoolWebsiteContentSchema.safeParse(value ?? {});
  return parsed.success ? parsed.data : schoolWebsiteContentSchema.parse({});
}

/** A post as the public site receives it. Upload paths are already rewritten to public media URLs. */
export interface PublicSchoolPost {
  id: string;
  kind: SchoolPostKind;
  title: string;
  summary: string | null;
  body: string | null;
  imageUrl: string | null;
  attachmentUrl: string | null;
  attachmentName: string | null;
  eventDate: string | null;
  location: string | null;
  publishedAt: string | null;
}

/** `GET /api/v1/school-website/public/:schoolCode` → `data`. */
export interface PublicSchoolSite {
  school: {
    name: string;
    code: string;
    logo: string | null;
    address: string | null;
    city: string | null;
    state: string | null;
    pincode: string | null;
    phone: string | null;
    phoneAlt: string | null;
    email: string | null;
    board: string | null;
    socials: Partial<Record<"website" | "facebook" | "instagram" | "twitter" | "linkedin" | "youtube", string>>;
  };
  template: SchoolWebsiteTemplate;
  content: SchoolWebsiteContent;
  posts: PublicSchoolPost[];
  loginUrl: string;
  /** CampusHub parent-app store links, for the site's "Get the app" link. */
  platform: {
    appLinks: { android: string | null; ios: string | null };
  };
  updatedAt: string;
}
